'use client';

import { useCallback, useMemo } from 'react';
import { Briefcase, Sparkles } from 'lucide-react';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import {
  OrganizationPicker,
  type OrganizationSummary,
} from '@kit/profiles/components';
import { Card, CardContent, CardDescription, CardTitle } from '@kit/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Switch } from '@kit/ui/switch';
import { Trans } from '@kit/ui/trans';

import {
  ListingFilterBar,
  ListingFilterSheet,
  useListingFilters,
} from '~/components/listing-filters';
import { auditionsFilterConfig } from '~/lib/listings/auditions-filter-config';
import { AuditionCard, type AuditionWithApplication } from './audition-card';

// Shared nuqs options for the surface-managed slot params (org/status/match):
// shallow:false re-runs the server loader, matching the shared filter hook.
const SLOT_OPTS = { shallow: false, scroll: false, history: 'push' } as const;

interface AuditionsPageContainerProps {
  auditions: AuditionWithApplication[];
  totalCount: number;
  appliedCount: number;
  /**
   * Organization (company) multi-select filter state. `selectedIds` seed the
   * picker on first paint; `initialSelected` are their resolved summaries so
   * chips render names + logos without a client round-trip. Ignored when
   * `enableOrgFilter` is false (competitions).
   */
  orgFilter: {
    selectedIds: string[];
    initialSelected: OrganizationSummary[];
  };
  /** Render the organization filter (auditions). Competitions pass false. */
  enableOrgFilter?: boolean;
  /**
   * When false, hides authenticated-only chrome (the applications metric,
   * "Applied" status filter). Anonymous visitors browse the same list.
   */
  isAuthenticated?: boolean;
  /**
   * Profile-match personalization state (signed-in dancers only). When present,
   * the "Match my profile" toggle renders and the Sort control offers "Best
   * match". Null → no personalization.
   */
  match?: {
    active: boolean;
    shownCount: number;
    eligibleCount: number;
    hiddenCount: number;
  } | null;
}

export function AuditionsPageContainer({
  auditions,
  totalCount,
  appliedCount,
  orgFilter,
  enableOrgFilter = true,
  isAuthenticated = false,
  match = null,
}: AuditionsPageContainerProps) {
  const { t } = useTranslation('auditions');

  const translate = useCallback(
    (key: string, defaultValue?: string) =>
      defaultValue ? t(key, { defaultValue }) : t(key),
    [t],
  );

  // Standard facets (type/gender) + sort + search go through the shared config
  // + hook. "Best match" sort only offered when a match profile exists.
  const config = useMemo(
    () => auditionsFilterConfig(translate, { includeBestMatch: !!match }),
    [translate, match],
  );
  const filters = useListingFilters(config);

  // A dancer-shared `?sort=best` link opened while signed out (or by a
  // non-dancer) has no matching Sort option (best-match is hidden without a
  // profile) — the Select would render blank. Normalize the DISPLAYED value
  // to 'nearest' (server ordering already falls back there in this case,
  // page.tsx `sortMode`), without touching the URL/hook state.
  const displaySort =
    !match && filters.state.sort === 'best' ? 'nearest' : filters.state.sort;

  // Surface-specific slot params — managed here (not the registry) via nuqs so
  // they preserve the hook's params and re-run the server loader.
  const [orgs, setOrgs] = useQueryState(
    'orgs',
    parseAsArrayOf(parseAsString).withDefault([]).withOptions(SLOT_OPTS),
  );
  const [status, setStatus] = useQueryState(
    'status',
    parseAsString.withOptions(SLOT_OPTS),
  );
  // Only the setter is used here — the server page reads ?showAll= to compute
  // `match.active`, which drives the toggle's checked state.
  const [, setShowAll] = useQueryState(
    'showAll',
    parseAsString.withOptions(SLOT_OPTS),
  );
  // "Show closed roles" toggle (R5). `?show=closed` widens the server loader to
  // include recently-closed auditions (greyed, non-applyable). Default off.
  const [show, setShow] = useQueryState(
    'show',
    parseAsString.withOptions(SLOT_OPTS),
  );
  const showClosed = show === 'closed';

  const hasAnyFilter =
    filters.activeCount > 0 ||
    !!filters.state.search ||
    (enableOrgFilter && orgs.length > 0) ||
    status === 'applied';

  // The shared bar's "Clear all" only resets the params `useListingFilters`
  // owns (search/sort/facets) — it has no visibility into the surface-managed
  // slot params (orgs/status/showAll). Compose a `clearAll` that resets both,
  // so the single Clear-all affordance actually clears everything the user
  // sees as "a filter" on this page.
  const filtersWithSlots = useMemo(
    () => ({
      ...filters,
      // Display-normalized sort (see displaySort above) — the URL/hook state
      // is untouched; only the Select's rendered value is corrected.
      state: { ...filters.state, sort: displaySort },
      clearAll: () => {
        filters.clearAll();
        if (enableOrgFilter) void setOrgs(null);
        void setStatus(null);
        void setShowAll(null);
        void setShow(null);
      },
    }),
    [
      filters,
      displaySort,
      enableOrgFilter,
      setOrgs,
      setStatus,
      setShowAll,
      setShow,
    ],
  );

  // The organization multi-select (leading slot) + auth-only status select.
  const leading = (
    <>
      {enableOrgFilter ? (
        <OrganizationPicker
          multiple
          value={orgs}
          initialSelected={orgFilter.initialSelected}
          onChange={(value) => {
            const ids = Array.isArray(value) ? value : value ? [value] : [];
            void setOrgs(ids.length > 0 ? ids : null);
          }}
          placeholder={t('filters.orgPlaceholder')}
          aria-label={t('filters.organizations')}
          className="w-full sm:w-[240px]"
        />
      ) : null}

      {isAuthenticated ? (
        <Select
          value={status ?? 'all'}
          onValueChange={(value) =>
            void setStatus(value === 'applied' ? 'applied' : null)
          }
        >
          <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[150px]">
            <SelectValue placeholder={t('filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <Trans i18nKey="auditions:filters.all" />
            </SelectItem>
            <SelectItem value="applied">
              <Trans i18nKey="auditions:filters.applied" />
            </SelectItem>
          </SelectContent>
        </Select>
      ) : null}
    </>
  );

  // The "Show closed roles" toggle — always available (R5). Off by default; on,
  // the feed also lists recently-closed auditions, greyed and non-applyable.
  const showClosedToggle = (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium">
      <Switch
        checked={showClosed}
        onCheckedChange={(checked) => void setShow(checked ? 'closed' : null)}
      />
      <Trans
        i18nKey="auditions:filters.showClosed"
        defaults="Show closed roles"
      />
    </label>
  );

  // Below-chips slot: the "Match my profile" personalization block (dancers
  // only) plus the always-on "show closed" toggle.
  const slot = (
    <div className="flex flex-col gap-3">
      {match ? (
        <div className="border-primary/20 bg-primary/5 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5">
          <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium">
            <Switch
              checked={match.active}
              onCheckedChange={(checked) =>
                void setShowAll(checked ? null : '1')
              }
            />
            <Sparkles className="text-primary size-4 shrink-0" aria-hidden />
            <Trans i18nKey="auditions:filters.matchMyProfile" />
          </label>
          <p className="text-muted-foreground text-sm">
            {match.active ? (
              <Trans
                i18nKey="auditions:match.narrowed"
                defaults="Showing {{count}} auditions matched to your profile."
                values={{ count: match.shownCount }}
              />
            ) : (
              <Trans
                i18nKey="auditions:match.showingAll"
                defaults="Showing all auditions. {{count}} match your profile."
                values={{ count: match.eligibleCount }}
              />
            )}
          </p>
        </div>
      ) : null}
      <div className="px-1">{showClosedToggle}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Compact metric line (replaces the old oversized stat tiles). */}
      <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 text-sm">
        <span className="text-foreground font-semibold">{totalCount}</span>
        <Trans i18nKey="auditions:stats.openAuditions" />
        {isAuthenticated ? (
          <>
            <span aria-hidden>·</span>
            <span className="text-foreground font-semibold">
              {appliedCount}
            </span>
            <Trans i18nKey="auditions:stats.myApplications" />
          </>
        ) : null}
      </p>

      {/* Web filter bar (≥sm) + mobile sheet (<sm). */}
      <div className="hidden sm:block">
        <ListingFilterBar
          config={config}
          filters={filtersWithSlots}
          resultCount={auditions.length}
          leading={leading}
          slot={slot}
        />
      </div>
      <div className="sm:hidden">
        <ListingFilterSheet
          config={config}
          filters={filtersWithSlots}
          leading={leading}
          slot={slot}
        />
      </div>

      {/* Auditions grid */}
      {auditions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="text-muted-foreground mb-4 h-12 w-12" />
            <CardTitle className="mb-2">
              <Trans i18nKey="auditions:empty.title" />
            </CardTitle>
            <CardDescription>
              {hasAnyFilter ? (
                <Trans i18nKey="auditions:empty.filtered" />
              ) : (
                <Trans i18nKey="auditions:empty.noOpen" />
              )}
            </CardDescription>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {auditions.map((audition) => (
            <AuditionCard key={audition.id} audition={audition} />
          ))}
        </div>
      )}
    </div>
  );
}
